import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgeCheck, X, ShoppingBag } from 'lucide-react';
interface SaleNotification {
  name: string;
  location: string;
  action: string;
  minutesAgo: number;
  flag: string;
}
const NOTIFICATIONS: SaleNotification[] = [{
  name: 'Nicolás',
  location: 'Ciudad de México',
  action: 'acaba de adquirir El Código Maestro',
  minutesAgo: 4,
  flag: '🇲🇽'
}, {
  name: 'Mateo',
  location: 'Buenos Aires, Argentina',
  action: 'se unió al protocolo',
  minutesAgo: 12,
  flag: '🇦🇷'
}, {
  name: 'Sebastián',
  location: 'Santiago, Chile',
  action: 'inició su transformación',
  minutesAgo: 7,
  flag: '🇨🇱'
}, {
  name: 'Santiago',
  location: 'Bogotá, Colombia',
  action: 'reclamó su acceso',
  minutesAgo: 2,
  flag: '🇨🇴'
}, {
  name: 'Diego',
  location: 'Lima, Perú',
  action: 'descargó el manual',
  minutesAgo: 9,
  flag: '🇵🇪'
}, {
  name: 'Joaquín',
  location: 'Montevideo, Uruguay',
  action: 'se unió al protocolo',
  minutesAgo: 15,
  flag: '🇺🇾'
}];
const ROTATION_INTERVAL_MS = 12000; // 12s between notifications
const INITIAL_DELAY_MS = 5000; // 5s after page load before first notification
export function LiveSalesNotification() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Initial delayed appearance
  useEffect(() => {
    if (dismissed) return;
    const initialTimer = window.setTimeout(() => setVisible(true), INITIAL_DELAY_MS);
    return () => window.clearTimeout(initialTimer);
  }, [dismissed]);
  // Rotation: hide -> swap content -> show again
  useEffect(() => {
    if (dismissed || !visible) return;
    const cycle = window.setInterval(() => {
      setVisible(false);
      // After exit animation, advance index and show next
      window.setTimeout(() => {
        setIndex(prev => (prev + 1) % NOTIFICATIONS.length);
        setVisible(true);
      }, 600);
    }, ROTATION_INTERVAL_MS);
    return () => window.clearInterval(cycle);
  }, [visible, dismissed]);
  if (dismissed) return null;
  const current = NOTIFICATIONS[index];
  return <div className="fixed bottom-4 left-4 z-40 pointer-events-none max-w-[calc(100vw-2rem)]" aria-live="polite" aria-atomic="true" data-id="element-95">
      <AnimatePresence mode="wait" data-id="element-96">
        {visible && <motion.div key={index} initial={{
        opacity: 0,
        x: -24,
        y: 10
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -24,
        y: 10
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }} className="pointer-events-auto" data-id="element-97">
            <div className="flex items-center gap-3 bg-[#1a1a1a]/95 backdrop-blur-md border border-white/10 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] pl-3 pr-4 py-3 sm:pl-3.5 sm:pr-5 sm:py-3.5 max-w-[320px] sm:max-w-[360px]" data-id="element-98">
              {/* Avatar with flag + shopping bag indicator */}
              <div className="relative flex-shrink-0" data-id="element-99">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-brand-gold/20 to-brand-gold/5 border border-brand-gold/30 flex items-center justify-center text-xl sm:text-2xl" data-id="element-100">
                  <span aria-hidden="true" data-id="element-101">{current.flag}</span>
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 sm:w-[18px] sm:h-[18px] rounded-full bg-brand-gold flex items-center justify-center border-[1.5px] border-[#1a1a1a]" data-id="element-102">
                  <ShoppingBag className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-brand-darker" strokeWidth={3} data-id="element-103" />
                </div>
              </div>

              {/* Message */}
              <div className="flex-1 min-w-0" data-id="element-104">
                <div className="flex items-center gap-1 mb-0.5" data-id="element-105">
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-wider font-semibold text-brand-gold/90" data-id="element-106">
                    Compra verificada
                  </span>
                  <BadgeCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-brand-gold" strokeWidth={2.5} data-id="element-107" />
                </div>
                <p className="text-[12px] sm:text-[13px] text-white/95 leading-snug font-medium" data-id="element-108">
                  <span className="font-bold" data-id="element-109">{current.name}</span>
                  <span className="text-white/75" data-id="element-110"> de {current.location} </span>
                  <span className="text-white/85" data-id="element-111">{current.action}</span>
                </p>
                <p className="text-[10px] sm:text-[11px] text-white/45 mt-1 font-medium" data-id="element-112">
                  hace {current.minutesAgo}{' '}
                  {current.minutesAgo === 1 ? 'minuto' : 'minutos'}
                  <span className="mx-1.5 text-white/25" data-id="element-113">·</span>
                  <span className="inline-flex items-center gap-1" data-id="element-114">
                    <span className="relative flex h-1.5 w-1.5" data-id="element-115">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" data-id="element-116"></span>
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" data-id="element-117"></span>
                    </span>
                    en vivo
                  </span>
                </p>
              </div>

              {/* Dismiss */}
              <button onClick={() => setDismissed(true)} aria-label="Cerrar notificación" className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors" data-id="element-118">
                <X className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-119" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}